/** The two-layer refresh, across a mount boundary: the caller invalidates, and the list page
 *  purges its own grid on mount off the Zustand flag. Standalone so a unit test can assert both
 *  side-effects without the route. See docs/AG_GRID_REFRESH_PATTERN.md. */

import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { server } from '@/constants';
import { agGridDatasourceQueryKey } from '@/lib/createAgGridDatasource';
import type { QueryClient } from '@tanstack/react-query';

export async function applyInstrumentRunsListRefresh(
  orgId: string,
  queryClient: QueryClient,
  setShouldRefreshInstrumentRuns: (val: boolean) => void,
): Promise<void> {
  // Layer 1 (React Query): invalidate every AG Grid datasource cache shard.
  // Key prefix matches the full ['ag-grid-datasource', url, requestBody] entries
  // stored by createAgGridDatasource — see src/lib/createAgGridDatasource.ts.
  await queryClient.invalidateQueries({
    queryKey: agGridDatasourceQueryKey(`${server}/orgs/${orgId}/instrument_runs/search`),
  });

  // Layer 1b: belt-and-suspenders invalidation for any non-AG-Grid consumers
  // that use the plain list key (e.g. future hooks, detail pre-fetches).
  await queryClient.invalidateQueries({
    queryKey: instrumentRunQueryKeys.list(orgId),
  });

  // Layer 2 (Zustand): flag consumed by index.tsx's useEffect.
  // The list page calls gridRef.api.refreshServerSide({ purge: true }) and
  // clears the flag — see routes/instrument_runs/index.tsx.
  setShouldRefreshInstrumentRuns(true);
}
